import { Graphics } from "pixi.js";

import { Colors } from "../config/DesignConstants";
import BaseEntity from "../core/entity/BaseEntity";
import Entity from "../core/entity/Entity";
import { createGraphics } from "../core/entity/GameSprite";
import { clamp, mod } from "../core/util/MathUtil";
import { EARTH_BITMAP, EARTH_BITMAP_SIZE } from "./EarthBitmap";
import { LevelEntity } from "./LevelEntity";

// Earth rendering constants
export const EARTH_RADIUS = 30;
const LINE_SPACING = 3; // distance between scan lines
const LINE_THICKNESS = 1.5;
const ROTATION_SPEED = 0.01; // radians per second
const TRANSITION_GAP = 0; // gap size at land/water boundaries
const MIN_SAMPLES = 128;
const SAMPLES_PER_PX = 8;
const RENDER_INTERVAL = 1 / 6; // render every 1/6 second (~20 frames at 120 FPS) to reduce memory pressure

export default class Earth extends LevelEntity implements Entity {
  sprite: NonNullable<BaseEntity["sprite"]> & Graphics;
  private rotationAngle: number = 0;

  constructor() {
    super();
    this.tags = ["earth"];

    // Create graphics for Earth
    this.sprite = createGraphics("background"); // Use background layer so it appears behind everything
    this.sprite.cacheAsTexture(false);
    this.sprite.position.set(0, 0); // Center at origin
  }

  async onAdd(): Promise<void> {
    // Render immediately when added
    this.renderScanLines();

    // Start the render loop
    this.scheduleNextRender();
  }

  private async scheduleNextRender(): Promise<void> {
    if (this.isDestroyed()) return;

    await this.waitRender(RENDER_INTERVAL);
    this.renderScanLines();
    this.scheduleNextRender(); // Schedule the next render
  }

  onTick(dt: number): void {
    // Update rotation angle
    this.rotationAngle += ROTATION_SPEED * dt;
  }

  private renderScanLines(): void {
    this.sprite.clear();

    // Calculate how many lines we need based on radius
    const maxY = Math.ceil(EARTH_RADIUS / LINE_SPACING) * LINE_SPACING;

    // Draw lines at fixed spacing intervals
    for (let y = -maxY; y <= maxY; y += LINE_SPACING) {
      const distanceFromCenter = Math.abs(y);

      // Only draw lines that are within the circle
      // if (distanceFromCenter <= EARTH_RADIUS) {
      // Calculate line half-width using circle equation: x = sqrt(r² - y²)
      const radiusSquared = EARTH_RADIUS * EARTH_RADIUS;
      const ySquared = distanceFromCenter * distanceFromCenter;

      // Ensure we don't get negative values due to floating point precision
      if (radiusSquared >= ySquared) {
        const halfWidth = Math.sqrt(radiusSquared - ySquared);
        this.drawScanLine(y, halfWidth);
      }
      // }
    }
  }

  private drawScanLine(y: number, halfWidth: number): void {
    // Sample the entire scan line at high resolution
    const samples = this.sampleScanLine(y, halfWidth);

    // Find runs of consecutive land/water
    const runs = this.findRuns(samples, halfWidth);

    // Draw each run as a single continuous line
    for (const run of runs) {
      const color = run.isLand ? Colors.GREEN : Colors.BLUE;
      this.sprite.moveTo(run.startX, y).lineTo(run.endX, y).stroke({
        color,
        width: LINE_THICKNESS,
        cap: "round",
      });
    }
  }

  private sampleScanLine(y: number, halfWidth: number): boolean[] {
    // Sample at high resolution across the entire scan line
    const numSamples = Math.max(
      MIN_SAMPLES,
      Math.floor(halfWidth * SAMPLES_PER_PX),
    );
    const samples: boolean[] = [];

    for (let i = 0; i < numSamples; i++) {
      const x = -halfWidth + (i / (numSamples - 1)) * (halfWidth * 2);
      samples.push(this.sampleEarth(x, y));
    }

    return samples;
  }

  private findRuns(
    samples: boolean[],
    halfWidth: number,
  ): Array<{ startX: number; endX: number; isLand: boolean }> {
    const runs: Array<{ startX: number; endX: number; isLand: boolean }> = [];

    if (samples.length === 0) return runs;

    let currentType = samples[0];
    let runStart = 0;

    for (let i = 1; i < samples.length; i++) {
      if (samples[i] !== currentType) {
        // End of current run, start of new run
        let startX =
          -halfWidth + (runStart / (samples.length - 1)) * (halfWidth * 2);
        let endX =
          -halfWidth + ((i - 1) / (samples.length - 1)) * (halfWidth * 2);

        // Add gap at transitions - shorten the run on both ends
        const runLength = endX - startX;
        if (runLength > TRANSITION_GAP * 2) {
          // Only add gaps if the run is long enough
          if (runStart > 0) {
            // Not the first run, add gap at start
            startX += TRANSITION_GAP / 2;
          }
          if (i < samples.length) {
            // Not the last run, add gap at end
            endX -= TRANSITION_GAP / 2;
          }
        }

        runs.push({
          startX,
          endX,
          isLand: currentType,
        });

        currentType = samples[i];
        runStart = i;
      }
    }

    // Handle the final run
    let startX =
      -halfWidth + (runStart / (samples.length - 1)) * (halfWidth * 2);
    let endX = halfWidth;

    // Add gap at start if this isn't the only run
    if (runStart > 0) {
      startX += TRANSITION_GAP / 2;
    }

    runs.push({
      startX,
      endX,
      isLand: currentType,
    });

    return runs;
  }

  private sampleEarth(x: number, y: number): boolean {
    // Convert y-coordinate to latitude (flipped so north pole is at top)
    const normalizedY = y / EARTH_RADIUS; // -1 to 1
    const bitmapY = Math.floor(((1 - normalizedY) / 2) * EARTH_BITMAP_SIZE);

    // Convert x-coordinate to longitude with horizontal rotation (globe spinning)
    const normalizedX = x / EARTH_RADIUS / 2; // -1 to 1

    // Apply horizontal rotation as longitude shift
    // Each full rotation (2π) should scroll through the entire longitude range
    const longitudeShift =
      (this.rotationAngle / (Math.PI * 2)) * EARTH_BITMAP_SIZE;
    const bitmapX = Math.floor(
      ((normalizedX + 1) / 2) * EARTH_BITMAP_SIZE + longitudeShift,
    );

    // Wrap longitude coordinate to create seamless horizontal scrolling
    const wrappedX = mod(bitmapX, EARTH_BITMAP_SIZE);

    // Clamp latitude to valid bitmap coordinates
    const clampedY = clamp(bitmapY, 0, EARTH_BITMAP_SIZE - 1);

    // Sample the bitmap (1 = land, 0 = water)
    return EARTH_BITMAP[clampedY][wrappedX] === 1;
  }
}
